import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calendar, ArrowRight, Download, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { updates } from '../data/mockData';

const SECTION_NAVY = '#0A1628';
const SECTION_DEEP = '#0F1A2B';
const LAUNCH_BRIEF_URL = '/documents/the-american-arsenal-kindle-launch-brief-clean-2026-06-29.pdf';
const AMAZON_SEARCH_URL = 'https://www.amazon.com/s?k=The+American+Arsenal+Vikrant+Salar';
const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@SALARTechnologies';

const youtubeLaunchUpdate = {
  id: 'salar-youtube-channel-live-2026-09-05',
  title: 'SALAR Technologies YouTube Channel Is Live',
  date: 'September 5, 2026',
  category: 'Company Update / Media',
  image: '/img/news/who-owns-poster.webp?v=4',
  imageAlt: 'SALAR Technologies Who Owns the Intelligence video visual for the official YouTube channel launch.',
  targetType: 'external',
  targetUrl: YOUTUBE_CHANNEL_URL,
  ctaLabel: 'Visit SALAR on YouTube',
  badgeLabel: 'External',
  openInNewTab: true,
  tags: ['YouTube', 'SALAR Technologies', 'AI Sovereignty', 'Semiconductors', 'AI Infrastructure', 'Engineering'],
  excerpt: 'The official SALAR Technologies YouTube channel is now live at @SALARTechnologies. The channel launches with the flagship video “Who Owns the Intelligence? AI, Chips, Cloud & the Fight for Technology Sovereignty,” together with short-form engineering visuals covering AI data centers, semiconductor supply chains, electronics reliability, hidden chip dependencies, and global supply-chain networks.',
  body: 'The official SALAR Technologies YouTube channel is now live at @SALARTechnologies. The channel expands SALAR’s public engineering and research communication across semiconductors, AI infrastructure, electronics reliability, supply-chain resilience, advanced manufacturing, and technology sovereignty. The launch includes the flagship video “Who Owns the Intelligence? AI, Chips, Cloud & the Fight for Technology Sovereignty” and a set of short-form technical videos designed to make complex engineering systems easier to understand.'
};

const launchUpdate = {
  id: 'american-arsenal-kindle-launch-2026-06-29',
  title: 'The American Arsenal Kindle Launch Brief',
  date: 'June 29, 2026',
  category: 'Publication',
  image: '/img/salar_hero_semiconductor_resilience.webp',
  imageAlt: 'Semiconductor resilience visual representing The American Arsenal Kindle launch brief.',
  targetType: 'external',
  targetUrl: AMAZON_SEARCH_URL,
  ctaLabel: 'Search on Amazon',
  badgeLabel: 'External',
  openInNewTab: true,
  downloadUrl: LAUNCH_BRIEF_URL,
  downloadLabel: 'Download Launch Brief',
  excerpt: 'SALAR Technologies LLC has released a public launch brief for The American Arsenal: Defense Electronics Supply-Chain Sovereignty, a strategic engineering atlas by Vikrant Salar on defense-electronics resilience, semiconductor supply-chain sovereignty, critical minerals, counterfeit prevention, manufacturing stability, AI-enabled assurance, and visible supply-chain intelligence. Kindle launch price: $19.99 USD. Available through Amazon Kindle marketplaces worldwide.',
  body: 'SALAR Technologies LLC has released a public launch brief for The American Arsenal: Defense Electronics Supply-Chain Sovereignty, a strategic engineering atlas by Vikrant Salar on defense-electronics resilience, semiconductor supply-chain sovereignty, critical minerals, counterfeit prevention, manufacturing stability, AI-enabled assurance, and visible supply-chain intelligence. Kindle launch price: $19.99 USD. Available through Amazon Kindle marketplaces worldwide.'
};

const TYPE_FROM_URL = (url) => {
  if (!url) return 'none';
  if (/^https?:\/\//i.test(url)) return 'external';
  if (/\.pdf($|\?)/i.test(url)) return 'pdf';
  if (url.startsWith('#')) return 'anchor';
  if (url === '/salar-resilience-terrain') return 'demo';
  return 'internal-page';
};

const BADGE_FROM_TYPE = {
  demo: 'Demo',
  pdf: 'PDF',
  external: 'External',
  anchor: 'Page',
  'internal-page': 'Page',
  none: null,
  page: 'Page',
  resource: 'Resource',
};

const BADGE_COLORS = {
  Demo: { bg: 'rgba(0,212,255,0.12)', border: 'rgba(0,212,255,0.55)', text: '#67E8F9' },
  PDF: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.55)', text: '#FCD34D' },
  Page: { bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.5)', text: '#93C5FD' },
  Resource: { bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.5)', text: '#C4B5FD' },
  External: { bg: 'rgba(74,222,128,0.12)', border: 'rgba(74,222,128,0.5)', text: '#86EFAC' },
};

function resolveNewsTarget(item) {
  let targetType = item.targetType;
  let targetUrl = item.targetUrl || item.link;
  let ctaLabel = item.ctaLabel;

  if (!targetUrl) {
    targetUrl = `/news-updates/${item.id}`;
    ctaLabel = ctaLabel || 'Read update';
    targetType = targetType || 'internal-page';
  }

  if (!targetType) targetType = TYPE_FROM_URL(targetUrl);
  if (!ctaLabel) ctaLabel = targetType === 'demo' ? 'Open demo page' : 'Read update';

  let badgeLabel = item.badgeLabel || BADGE_FROM_TYPE[targetType] || 'Page';
  if (badgeLabel === 'Page' && /publication|research/i.test(item.category || '')) badgeLabel = 'Resource';
  if (badgeLabel === 'Page' && /interactive demo|prototype/i.test(item.category || '')) badgeLabel = 'Demo';

  const openInNewTab = item.openInNewTab ?? (targetType === 'pdf' || targetType === 'external');
  const downloadUrl = item.downloadUrl || null;
  const downloadLabel = item.downloadLabel || (downloadUrl ? 'Download PDF' : null);

  return { targetType, targetUrl, ctaLabel, badgeLabel, openInNewTab, downloadUrl, downloadLabel };
}

const NewsCard = ({ item }) => {
  const { targetUrl, ctaLabel, badgeLabel, openInNewTab, downloadUrl, downloadLabel } =
    useMemo(() => resolveNewsTarget(item), [item]);

  const isExternalAnchor = openInNewTab || /^https?:\/\//.test(targetUrl) || /\.pdf($|\?)/i.test(targetUrl);
  const badgeColors = BADGE_COLORS[badgeLabel] || BADGE_COLORS.Page;
  const headlineId = `news-headline-${item.id}`;

  const MainWrapper = ({ children }) =>
    isExternalAnchor ? (
      <a
        href={targetUrl}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        aria-label={`Read update: ${item.title}`}
        aria-describedby={headlineId}
        className="block group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded-md"
        data-testid={`news-card-link-${item.id}`}
      >
        {children}
      </a>
    ) : (
      <Link
        to={targetUrl}
        aria-label={`Read update: ${item.title}`}
        aria-describedby={headlineId}
        className="block group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded-md"
        data-testid={`news-card-link-${item.id}`}
      >
        {children}
      </Link>
    );

  return (
    <article
      data-testid={`news-card-${item.id}`}
      className="relative rounded-2xl overflow-hidden transition-all hover:-translate-y-0.5 motion-reduce:hover:translate-y-0"
      style={{ background: 'rgba(13, 31, 55, 0.88)', border: '1px solid rgba(0, 212, 255, 0.25)', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}
    >
      <MainWrapper>
        <div className={`flex flex-col ${item.image ? 'md:flex-row' : ''}`}>
          {item.image && (
            <div
              className="md:w-72 md:flex-shrink-0 h-48 md:h-auto"
              style={{ backgroundImage: `linear-gradient(rgba(10,22,40,0.35), rgba(10,22,40,0.6)), url('${item.image}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
              data-testid={`news-image-${item.id}`}
              role="img"
              aria-label={item.imageAlt || item.title}
            />
          )}
          <div className="flex-1 p-6 md:p-7">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge variant="outline" className="border-cyan-500/40 text-cyan-200 text-[11px] tracking-wide">
                {item.category}
              </Badge>
              <span
                data-testid={`news-destination-badge-${item.id}`}
                className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border"
                style={{ background: badgeColors.bg, borderColor: badgeColors.border, color: badgeColors.text }}
              >
                {badgeLabel}
              </span>
              <div className="flex items-center text-[11px] font-semibold tracking-wider ml-auto" style={{ color: '#00D4FF' }}>
                <Calendar size={13} className="mr-1.5" />
                {item.date}
              </div>
            </div>
            <h2 id={headlineId} className="text-xl md:text-2xl font-bold mb-3 leading-snug transition-colors" style={{ color: '#35D7FF' }} data-testid={`news-title-${item.id}`}>
              <span className="group-hover:text-white transition-colors">{item.title}</span>
            </h2>
            <p className="text-blue-100 leading-relaxed text-sm md:text-base">{item.excerpt || item.body}</p>
            <div className="flex items-center gap-2 mt-5">
              <span className="inline-flex items-center text-cyan-300 font-bold text-sm group-hover:text-white transition-colors" data-testid={`news-cta-${item.id}`}>
                {ctaLabel}
                <ArrowRight size={16} className="ml-1.5 transition-transform group-hover:translate-x-1 motion-reduce:group-hover:translate-x-0" />
                {openInNewTab && <ExternalLink size={13} className="ml-1.5 opacity-70" aria-hidden="true" />}
              </span>
            </div>
          </div>
        </div>
      </MainWrapper>
      {downloadUrl && (
        <div className="px-6 md:px-7 pb-6 md:pb-7 -mt-2">
          <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid={`news-download-${item.id}`} aria-label={`Download PDF for ${item.title}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold transition-colors" style={{ borderColor: 'rgba(0,212,255,0.5)', color: '#67E8F9', background: 'rgba(0,212,255,0.04)' }} onClick={(e) => e.stopPropagation()}>
            <Download size={14} />
            {downloadLabel}
          </a>
        </div>
      )}
    </article>
  );
};

const Updates = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const allUpdates = useMemo(() => [youtubeLaunchUpdate, launchUpdate, ...updates], []);
  const categories = [...new Set(allUpdates.map((u) => u.category))];
  const visibleUpdates = activeCategory ? allUpdates.filter((u) => u.category === activeCategory) : allUpdates;

  return (
    <div className="min-h-screen" style={{ background: SECTION_NAVY }} data-testid="updates-page">
      <Helmet>
        <title>Updates &amp; Insights | SALAR Technologies LLC</title>
        <meta name="description" content="Latest SALAR Technologies announcements, capability launches, publications, and public-safe research commentary." />
        <meta property="og:title" content="SALAR Technologies - Updates & Insights" />
        <meta property="og:description" content="Capability launches, publications, compliance milestones, and research commentary from SALAR Technologies LLC." />
        <meta property="og:url" content="https://www.salartechnologies.com/news-updates" />
        <link rel="canonical" href="https://www.salartechnologies.com/news-updates" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <section className="text-white relative" style={{ backgroundImage: `linear-gradient(rgba(10,22,40,0.7), rgba(10,22,40,0.9)), url('/img/ai_fabric_hero.jpg')`, backgroundSize: 'cover', backgroundPosition: 'center', minHeight: 240, display: 'flex', alignItems: 'center', padding: '48px 0' }} data-testid="updates-hero">
        <div className="container mx-auto px-6 w-full">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="border-cyan-400 text-cyan-300 mb-4 tracking-widest text-xs">NEWS &amp; UPDATES</Badge>
            <h1 className="text-4xl lg:text-5xl font-bold mb-3">Updates &amp; Insights</h1>
            <p className="text-base text-blue-100 leading-relaxed max-w-3xl mx-auto">Company announcements, publications, capability launches, patent portfolio milestones, and public-safe research commentary from SALAR Technologies LLC.</p>
          </div>
        </div>
      </section>
      <section className="py-10 border-y border-blue-800/30" style={{ background: SECTION_DEEP }}>
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-cyan-300 text-xs font-bold tracking-widest text-center mb-4">FILTER BY CATEGORY</div>
            <div className="flex flex-wrap justify-center gap-2" role="toolbar" aria-label="Filter news by category">
              <button type="button" onClick={() => setActiveCategory(null)} aria-pressed={activeCategory === null} data-testid="filter-all" className={`px-4 py-1.5 text-xs tracking-wide rounded-full border transition-colors ${activeCategory === null ? 'bg-cyan-600 border-cyan-400 text-white' : 'border-cyan-500/40 text-cyan-200 hover:border-cyan-300'}`}>All</button>
              {categories.map((category) => {
                const isActive = activeCategory === category;
                return (
                  <button key={category} type="button" onClick={() => setActiveCategory(isActive ? null : category)} aria-pressed={isActive} data-testid={`filter-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className={`px-4 py-1.5 text-xs tracking-wide rounded-full border transition-colors ${isActive ? 'bg-cyan-600 border-cyan-400 text-white' : 'border-cyan-500/40 text-cyan-200 hover:border-cyan-300'}`}>
                    {category}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <section className="py-16" style={{ background: SECTION_NAVY }}>
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto space-y-6">
            {visibleUpdates.map((update) => <NewsCard key={update.id} item={update} />)}
          </div>
        </div>
      </section>
      <section className="py-16 text-white" style={{ background: `linear-gradient(135deg, ${SECTION_NAVY} 0%, ${SECTION_DEEP} 100%)` }} data-testid="updates-cta">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Informed</h2>
            <p className="text-base text-blue-100 leading-relaxed mb-8 max-w-2xl mx-auto">For capability launches, publications, patent portfolio milestones, and program-office briefings, contact SALAR directly.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact"><Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold" data-testid="updates-contact-btn">Contact SALAR<ArrowRight className="ml-2" size={18} /></Button></Link>
              <a href="mailto:contact@salartechnologies.com?subject=Subscribe%20to%20SALAR%20Updates" data-testid="updates-subscribe-btn"><Button size="lg" variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]">Subscribe by Email</Button></a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Updates;
export { resolveNewsTarget };
